import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { t } from "@/i18n";
import { Radio, ShieldCheck, AlertCircle } from "lucide-react";

export default function LoginPage({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [googleStatus, setGoogleStatus] = useState({ available: false, url: null, reason_nl: null });
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isSignup = mode === "signup";

  useEffect(() => {
    api.get("/auth/google/status")
      .then((r) => setGoogleStatus(r.data))
      .catch(() => setGoogleStatus({ available: false, reason_nl: t("auth.google_soon") }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const data = isSignup
        ? await signup({ email, password, name: name || null, organization_name: orgName })
        : await login(email, password);
      const to = data?.organization?.intake_completed ? "/" : "/intake";
      nav(loc.state?.from?.pathname || to, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bg-canvas text-text-primary" style={{ fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}>
      {/* Left: branding panel — warm wit met subtiel patroon */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border-hairline bg-bg-panel relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #172C32 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />

        <div className="relative">
          <Logo variant="horizontal" size={40} showTagline />
        </div>

        <div className="hidden" />
        <div className="relative space-y-6">
          <h1 className="text-4xl xl:text-5xl font-bold text-text-primary tracking-tight leading-[1.1]">
            {t("auth.headline")}
          </h1>
          <p className="text-text-muted text-lg max-w-md leading-relaxed">
            {t("auth.subhead")}
          </p>
          <div className="grid gap-2 max-w-md">
            <TickerRow label="Overzicht" value="Alle marketing op één plek" status="muted" mono />
            <TickerRow label="Merkstem" value="Wordt automatisch geleerd" status="muted" mono />
            <TickerRow label="Pagina-focus" value="Top 10 met verbeter-kans" status="muted" mono />
            <TickerRow label="Berichten" value="Kant-en-klaar per week" status="muted" mono />
          </div>
        </div>

        <div className="relative text-xs text-text-subtle">
          © JustEasySEO · {t("app.subtitle")} — {t("app.ghost_active_hint")}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="lg:hidden mb-6 flex items-center gap-2">
              <Logo variant="horizontal" size={26} />
            </div>
            <h2 className="text-2xl font-display font-bold text-text-primary tracking-tight">
              {isSignup ? t("auth.signup_title") : t("auth.login_title")}
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {isSignup ? "Start uw JustEasySEO-account." : t("auth.welcome_back")}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <>
                <Field label={t("auth.name")}>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="auth-name-input"
                    className="w-full h-11 px-3 rounded-md bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                </Field>
                <Field label={t("auth.organization_name")}>
                  <input
                    type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} required
                    data-testid="auth-org-input"
                    className="w-full h-11 px-3 rounded-md bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                </Field>
              </>
            )}
            <Field label={t("auth.email")}>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="auth-email-input"
                className="w-full h-11 px-3 rounded-md bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </Field>
            <Field label={t("auth.password")}>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                data-testid="auth-password-input"
                className="w-full h-11 px-3 rounded-md bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </Field>
            {!isSignup && (
              <div className="text-right -mt-1">
                <a href="/wachtwoord-vergeten"
                    data-testid="auth-forgot-password-link"
                    className="text-xs text-blue-600 hover:text-blue-700 underline">
                  Wachtwoord vergeten?
                </a>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="px-3 py-2 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{typeof error === "string" && error.length < 80 ? error : t("auth.error_invalid")}</span>
                </div>
                {typeof error === "string" && error.length < 80 && error !== t("auth.error_invalid") && (
                  <div className="mt-1 pl-6 text-[11px] text-red-500/70">
                    {t("auth.tech_code_prefix")}: {error}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="auth-submit-button"
              className="w-full h-11 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold transition-colors shadow-sm"
            >
              {busy ? t("common.loading") : (isSignup ? t("auth.submit_signup") : t("auth.submit_login"))}
            </button>
          </form>

          {/* Google SSO */}
          <div className="mt-4">
            {googleStatus.available ? (
              <a
                href={googleStatus.url}
                data-testid="auth-google-sso-button"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const r = await api.get("/auth/google/start");
                    if (r.data?.available && r.data.authorization_url) {
                      window.location.href = r.data.authorization_url;
                    }
                  } catch (err) {
                    // Klant-vriendelijke B1-melding, geen HTTP-codes.
                    setError("Aanmelden met Google lukte even niet. Probeer het opnieuw.");
                  }
                }}
                className="w-full h-11 rounded-md border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-800 font-medium flex items-center justify-center gap-3 transition-colors shadow-sm cursor-pointer"
              >
                <GoogleIcon />
                {t("auth.google_continue")}
              </a>
            ) : (
              <button
                type="button" disabled
                data-testid="auth-google-sso-button-disabled"
                className="w-full h-11 rounded-md border border-neutral-200 bg-neutral-100 text-neutral-400 font-medium flex items-center justify-center gap-3 cursor-not-allowed"
                title={googleStatus.reason_nl}
              >
                <GoogleIcon muted />
                {t("auth.google_soon")}
              </button>
            )}
            <div className="flex items-start gap-2 mt-3 text-[11px] text-neutral-500 leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
              <span>{t("auth.google_notice_short")}</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setMode(isSignup ? "login" : "signup")}
              data-testid="auth-toggle-mode-button"
              className="text-sm text-neutral-500 hover:text-blue-600 transition-colors"
            >
              {isSignup ? t("auth.to_login") : t("auth.to_signup")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-wider text-neutral-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function TickerRow({ label, value, hint, status, mono }) {
  const dot =
    status === "green" ? "text-emerald-600"
    : status === "amber" ? "text-amber-500"
    : "text-neutral-400";
  return (
    <div className="flex items-center justify-between px-4 py-2.5 rounded-md border border-neutral-200 bg-neutral-50/50">
      <div className="flex items-center gap-2">
        <Radio className={`w-3 h-3 ${dot}`} />
        <span className="text-[10px] text-neutral-500">{label}</span>
      </div>
      <div className="text-right">
        <div className={`${mono ? "" : ""} text-sm text-neutral-900 tracking-tight font-semibold`}>{value}</div>
        {hint && <div className="text-[10px] text-neutral-400">{hint}</div>}
      </div>
    </div>
  );
}

function GoogleIcon({ muted }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" style={{ opacity: muted ? 0.4 : 1 }}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
